import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getFriendsCount, getFriends } from '../utils/friendsService';

/**
 * Pobiera liczbę oczekujących zaproszeń
 */
async function getPendingInvitationsCount(userId) {
  try {
    const { count, error } = await supabase
      .from('friend_invitations')
      .select('*', { count: 'exact', head: true })
      .eq('receiver_id', userId)
      .eq('status', 'pending');

    if (error) {
      console.error('Error fetching pending invitations count:', error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error('Error fetching pending invitations count:', error);
    return 0;
  }
}

/** Oczekujące zaproszenia wysłane przez użytkownika (status pending) */
async function getPendingSentInvitationsCount(userId) {
  try {
    const { count, error } = await supabase
      .from('friend_invitations')
      .select('*', { count: 'exact', head: true })
      .eq('sender_id', userId)
      .eq('status', 'pending');

    if (error) {
      console.error('Error fetching pending sent invitations count:', error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error('Error fetching pending sent invitations count:', error);
    return 0;
  }
}

/**
 * Hook do zarządzania danymi znajomych w czasie rzeczywistym
 * Automatycznie aktualizuje liczbę znajomych i listę znajomych
 * 
 * @param {string} userId - ID użytkownika
 * @returns {Object} { friendsCount, friends, pendingCount, pendingSentCount, isLoading, error }
 */
export function useFriendsRealtimeData(userId) {
  const [friendsCount, setFriendsCount] = useState(0);
  const [friends, setFriends] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingSentCount, setPendingSentCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const channelRef = useRef(null);

  // Ładowanie początkowych danych
  useEffect(() => {
    if (!userId) return;

    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [countResult, friendsResult, pendingResult, pendingSentResult] = await Promise.all([
          getFriendsCount(userId),
          getFriends(userId),
          getPendingInvitationsCount(userId),
          getPendingSentInvitationsCount(userId),
        ]);

        if (countResult.success) {
          setFriendsCount(countResult.count || 0);
        } else {
          throw new Error(countResult.error || 'Error loading friends count');
        }

        if (friendsResult.success) {
          setFriends(friendsResult.data || []);
        } else {
          console.error('Error loading friends:', friendsResult.error);
        }

        setPendingCount(pendingResult);
        setPendingSentCount(pendingSentResult);
      } catch (err) {
        console.error('Error loading friends data:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [userId]);

  // Subskrypcja real-time dla zmian w zaproszeniach znajomych
  useEffect(() => {
    if (!userId) return;

    // Clean up existing subscription
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Subscribe to changes in friend_invitations
    // Nasłuchuj zmian gdzie użytkownik jest senderem lub receiverem
    const channel = supabase
      .channel(`friends-realtime-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'friend_invitations',
          filter: `sender_id=eq.${userId}`
        },
        async (payload) => {
          // Przeładuj dane gdy nastąpi zmiana
          const [countResult, friendsResult, pendingResult, pendingSentResult] = await Promise.all([
            getFriendsCount(userId),
            getFriends(userId),
            getPendingInvitationsCount(userId),
            getPendingSentInvitationsCount(userId),
          ]);

          if (countResult.success && countResult.count !== null) {
            setFriendsCount(countResult.count);
          }

          if (friendsResult.success) {
            setFriends(friendsResult.data || []);
          }

          setPendingCount(pendingResult);
          setPendingSentCount(pendingSentResult);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'friend_invitations',
          filter: `receiver_id=eq.${userId}`
        },
        async (payload) => {
          // Przeładuj dane gdy nastąpi zmiana
          const [countResult, friendsResult, pendingResult, pendingSentResult] = await Promise.all([
            getFriendsCount(userId),
            getFriends(userId),
            getPendingInvitationsCount(userId),
            getPendingSentInvitationsCount(userId),
          ]);

          if (countResult.success && countResult.count !== null) {
            setFriendsCount(countResult.count);
          }

          if (friendsResult.success) {
            setFriends(friendsResult.data || []);
          }

          setPendingCount(pendingResult);
          setPendingSentCount(pendingSentResult);
        }
      )
      .subscribe();

    channelRef.current = channel;

    // Cleanup
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [userId]);

  return {
    friendsCount,
    friends,
    pendingCount,
    pendingSentCount,
    isLoading,
    error
  };
}

export default useFriendsRealtimeData;
